// FounderAudioButton — compact audio player embedded in the "Nuestros
// Fundadores" section. Each founder gets an independent instance
// bound to their own audio slot (see /lib/foundersAudio.js).
//
// States rendered:
//   • disabled — no audioSrc yet → grey pill with "Voice message
//     coming soon" copy so operators know the structure is in place.
//   • idle     — audio ready, not started
//   • loading  — buffering (first tap on a slow connection)
//   • playing  — audio is playing; timer + progress bar animate
//   • paused   — user tapped pause before the end
//   • finished — reached the end; primary action becomes "Play again"
//   • error    — audio failed to load / decode
//
// Global coordination — a module-level variable holds a reference to
// the currently-playing <audio> element. When any button starts a new
// track, it first pauses the previous one so only ONE founder message
// plays at a time (site-wide).

import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Volume2, AlertCircle, Loader2 } from "lucide-react";

// Currently-playing HTMLAudioElement. Shared across every mounted
// FounderAudioButton via module scope. `null` when nothing is playing.
let currentAudioEl = null;

const COPY = {
  listen: {
    en: "Play thank-you message",
    fr: "Écouter le message de remerciement",
    es: "Escuchar mensaje de agradecimiento",
  },
  playing: {
    en: "Now playing",
    fr: "En cours de lecture",
    es: "Reproduciendo",
  },
  paused: {
    en: "Paused",
    fr: "En pause",
    es: "En pausa",
  },
  finished: {
    en: "Play again",
    fr: "Réécouter",
    es: "Reproducir de nuevo",
  },
  coming: {
    en: "Voice message coming soon",
    fr: "Message vocal bientôt disponible",
    es: "Mensaje de voz próximamente",
  },
  loading: {
    en: "Loading…",
    fr: "Chargement…",
    es: "Cargando…",
  },
  error: {
    en: "Unable to load audio",
    fr: "Impossible de charger l'audio",
    es: "No se pudo cargar el audio",
  },
  pauseAria: { en: "Pause", fr: "Pause", es: "Pausar" },
  playAria: { en: "Play", fr: "Lire", es: "Reproducir" },
  replayAria: { en: "Play again", fr: "Réécouter", es: "Reproducir de nuevo" },
};

const pick = (obj, lang) => obj[lang] || obj.en;

const formatTime = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const FounderAudioButton = ({ audioSrc, founderId, name, lang = "en" }) => {
  const audioRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | finished | error
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const hasAudio = typeof audioSrc === "string" && audioSrc.trim().length > 0;
  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const testidBase = `founder-audio-${founderId}`;

  // Wire up media events so React state stays in sync with the browser's
  // playback state (including OS-level media keys and background pauses).
  useEffect(() => {
    if (!hasAudio) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    const onLoadedMeta = () => setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    const onTime = () => setCurrentTime(el.currentTime || 0);
    const onPlay = () => setState("playing");
    const onPause = () => {
      // `ended` fires pause internally; keep the finished state so the
      // UI shows the Replay affordance instead of a plain "Paused".
      setState((prev) => (prev === "finished" ? "finished" : "paused"));
    };
    const onEnded = () => {
      setState("finished");
      setCurrentTime(el.duration || 0);
      if (currentAudioEl === el) currentAudioEl = null;
    };
    const onWaiting = () => setState((prev) => (prev === "playing" ? "playing" : "loading"));
    const onCanPlay = () => setState((prev) => (prev === "loading" ? "idle" : prev));
    const onError = () => {
      setState("error");
      if (currentAudioEl === el) currentAudioEl = null;
    };
    el.addEventListener("loadedmetadata", onLoadedMeta);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMeta);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("error", onError);
    };
  }, [hasAudio, audioSrc]);

  // Release the module-level coordinator ref when this instance unmounts
  // while playing, otherwise a stale reference could block the next play.
  useEffect(() => {
    return () => {
      const el = audioRef.current;
      if (el && currentAudioEl === el) {
        currentAudioEl = null;
      }
    };
  }, []);

  const togglePlay = useCallback(() => {
    if (!hasAudio) return;
    const el = audioRef.current;
    if (!el) return;

    // If we're finished, rewind to the start so the same tap kicks off
    // a fresh listen — mirrors the "Play again" affordance.
    if (state === "finished") {
      el.currentTime = 0;
    }

    if (el.paused) {
      // Pause any other founder's audio that might be playing first.
      if (currentAudioEl && currentAudioEl !== el && !currentAudioEl.paused) {
        currentAudioEl.pause();
      }
      currentAudioEl = el;
      const p = el.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => setState("error"));
      }
      setState((prev) => (prev === "finished" ? "playing" : prev === "idle" ? "loading" : "playing"));
    } else {
      el.pause();
    }
  }, [hasAudio, state]);

  // Localised copy for the primary status line above the founder name.
  const statusCopy = (() => {
    if (state === "error") return pick(COPY.error, lang);
    if (state === "loading") return pick(COPY.loading, lang);
    if (state === "playing") return pick(COPY.playing, lang);
    if (state === "paused") return pick(COPY.paused, lang);
    if (state === "finished") return pick(COPY.finished, lang);
    return pick(COPY.listen, lang);
  })();

  // ── Disabled / coming-soon state ─────────────────────────────────
  if (!hasAudio) {
    return (
      <div
        className="mt-8 md:mt-10 inline-flex items-center gap-3 px-4 py-3 border border-[#C16542]/25 bg-[#C16542]/[0.04] opacity-80"
        aria-disabled="true"
        data-testid={`${testidBase}-disabled`}
      >
        <span className="w-10 h-10 rounded-full border border-[#C16542]/40 inline-flex items-center justify-center text-[#C16542]/60 shrink-0">
          <Volume2 className="w-4 h-4" strokeWidth={1.6} />
        </span>
        <span className="flex flex-col min-w-0">
          <span className="text-[9px] tracking-[0.28em] uppercase text-[#C16542]/70">
            {pick(COPY.coming, lang)}
          </span>
          <span className="text-sm text-[#3D342D]/70 truncate italic">{name}</span>
        </span>
      </div>
    );
  }

  // ── Active player ─────────────────────────────────────────────────
  const isBusy = state === "loading";
  const iconClass = "w-4 h-4";
  const primaryIcon = (() => {
    if (isBusy) return <Loader2 className={`${iconClass} animate-spin`} strokeWidth={1.8} />;
    if (state === "error") return <AlertCircle className={iconClass} strokeWidth={1.8} />;
    if (state === "playing") return <Pause className={iconClass} strokeWidth={1.8} />;
    if (state === "finished") return <RotateCcw className={iconClass} strokeWidth={1.8} />;
    return <Play className={`${iconClass} ml-0.5`} strokeWidth={1.8} />;
  })();

  const primaryAria = (() => {
    if (state === "playing") return pick(COPY.pauseAria, lang);
    if (state === "finished") return pick(COPY.replayAria, lang);
    return pick(COPY.playAria, lang);
  })();

  return (
    <div
      className="mt-8 md:mt-10 flex flex-col gap-2.5 px-4 py-3.5 border border-[#C16542]/40 bg-[#C16542]/[0.06]"
      data-testid={testidBase}
      data-state={state}
    >
      {/* Hidden <audio> element — actual playback driver. `preload="metadata"`
          fetches just the duration byte on mount without downloading the
          whole file, so both cards stay light on data. */}
      <audio
        ref={audioRef}
        src={audioSrc}
        preload="metadata"
        data-testid={`${testidBase}-el`}
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlay}
          disabled={isBusy || state === "error"}
          aria-label={`${primaryAria} — ${name}`}
          className="w-10 h-10 rounded-full bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] inline-flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F5EDDF] disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
          data-testid={`${testidBase}-toggle`}
        >
          {primaryIcon}
        </button>

        <div className="flex-1 min-w-0">
          <p
            className="text-[9px] tracking-[0.28em] uppercase text-[#C16542]"
            data-testid={`${testidBase}-status`}
          >
            {statusCopy}
          </p>
          <p className="text-sm text-[#3D342D] font-medium truncate">{name}</p>
        </div>

        <span
          className="text-[10px] tabular-nums text-[#5C5248] tracking-wider shrink-0"
          data-testid={`${testidBase}-time`}
        >
          {formatTime(currentTime)}
          <span className="text-[#5C5248]/50"> / </span>
          {duration > 0 ? formatTime(duration) : "—:—"}
        </span>
      </div>

      {/* Progress bar — 1 px terracotta fill against a soft cream track.
          Kept purely visual (non-seekable) to match the compact,
          non-interactive spirit of the section. Guests get a felt sense
          of how long the message is without competing with the letter's
          typography. */}
      <div
        className="w-full h-1 bg-[#3D342D]/10 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        aria-label={`${name} audio progress`}
        data-testid={`${testidBase}-progress`}
      >
        <div
          className="h-full bg-[#C16542] transition-[width] duration-200 ease-linear"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
};

export default FounderAudioButton;
